import React, { useState, useRef } from "react";
import { QrCode, CreditCard, Send, CheckCircle, Receipt, Upload, X } from "lucide-react";
import { UnifiedRecord } from "../../../types";
import { useAuth } from "../../../hooks/useAuth";
import { getRoleForEmail } from "../../../config/roles";

interface Props {
  records: UnifiedRecord[];
}

export function PaymentDashboard({ records }: Props) {
  const { user } = useAuth();
  const userRole = getRoleForEmail(user?.email);
  const isAdmin = userRole === "admin";

  const [selectedPatientId, setSelectedPatientId] = useState("");
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  
  // Local QR image state (persisted to localStorage for demo purposes)
  const [qrImage, setQrImage] = useState<string | null>(() => {
    return localStorage.getItem("hospital_qr_code") || null;
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Local state to track payments in current session since we lack a backend
  const [localPayments, setLocalPayments] = useState<Record<string, number>>({});
  const [transactionHistory, setTransactionHistory] = useState<{ id: string, name: string, amount: number, utr: string, time: string }[]>([]);

  const selectedPatient = records.find(r => r.patient_id === selectedPatientId);
  const baseDue = selectedPatient ? (selectedPatient.total_charge || 0) - (selectedPatient.amount_paid || 0) : 0;
  const currentDueAmount = baseDue - (selectedPatient ? (localPayments[selectedPatient.patient_id] || 0) : 0);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatientId || !amount || !utr) return;
    
    const parsedAmount = parseFloat(amount);
    
    setLocalPayments(prev => ({
      ...prev,
      [selectedPatientId]: (prev[selectedPatientId] || 0) + parsedAmount
    }));

    setTransactionHistory(prev => [
      {
        id: selectedPatientId,
        name: selectedPatient?.patient_name || "Unknown",
        amount: parsedAmount,
        utr: utr,
        time: new Date().toLocaleTimeString()
      },
      ...prev
    ]);

    setSuccessMessage(`Payment of ₹${parsedAmount.toLocaleString()} via UTR ${utr} recorded successfully.`);
    setAmount("");
    setUtr("");
    
    setTimeout(() => {
      setSuccessMessage("");
    }, 4000);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setQrImage(base64String);
        localStorage.setItem("hospital_qr_code", base64String); // Mock persistence
      };
      reader.readAsDataURL(file);
    }
  };

  const handleClearImage = () => {
    setQrImage(null);
    localStorage.removeItem("hospital_qr_code");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-7xl mx-auto pb-12">
      
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/5 pb-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-500/20 flex items-center justify-center border border-indigo-500/30 shadow-[0_0_15px_rgba(99,102,241,0.3)]">
            <CreditCard className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white tracking-wide">Payment & Settlement</h2>
            <p className="text-gray-400 text-sm">Upload your QR code and accept UPI payments instantly to clear dues.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* Left Column: QR Code Display */}
        <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 flex flex-col items-center shadow-xl">
          <div className="w-16 h-16 rounded-full bg-indigo-500/20 flex items-center justify-center mb-4">
            <QrCode className="w-8 h-8 text-indigo-400" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2 text-center">Hospital UPI QR Code</h3>
          <p className="text-gray-400 text-sm text-center mb-8">Scan to pay dues directly to the hospital account.</p>
          
          <div className="w-72 h-72 rounded-3xl border-2 border-dashed border-indigo-500/30 bg-black/40 flex items-center justify-center overflow-hidden relative group transition-colors hover:border-indigo-500/60">
            {qrImage ? (
              <>
                <img src={qrImage} alt="Uploaded QR Code" className="w-full h-full object-contain bg-white p-2" />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-500 w-full h-full">
                <QrCode className="w-10 h-10 mb-3 text-gray-700" />
                <span className="text-sm font-medium">QR Code not uploaded</span>
              </div>
            )}
          </div>
          
          {/* Admin Upload Controls - Displayed below the QR */}
          {isAdmin && (
            <div className="mt-4 flex gap-3">
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-2 bg-indigo-500/20 hover:bg-indigo-500/40 text-indigo-400 border border-indigo-500/30 py-2 px-4 rounded-xl text-sm transition-all"
              >
                <Upload className="w-4 h-4" /> {qrImage ? "Change QR" : "Upload QR"}
              </button>
              
              {qrImage && (
                <button 
                  onClick={handleClearImage}
                  className="flex items-center gap-2 bg-red-500/20 hover:bg-red-500/40 text-red-400 border border-red-500/30 py-2 px-4 rounded-xl text-sm transition-all"
                >
                  <X className="w-4 h-4" /> Remove
                </button>
              )}
              <input 
                type="file" 
                ref={fileInputRef} 
                className="hidden" 
                accept="image/*"
                onChange={handleImageUpload}
              />
            </div>
          )}
        </div>

        {/* Right Column: Settlement Form */}
        <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-xl relative">
          
          <div className="mb-6">
            <h3 className="text-2xl font-bold text-teal-400 flex items-center gap-2 mb-2">
              <Receipt className="w-6 h-6" /> Settle Dues
            </h3>
            <p className="text-gray-400 text-sm">Select patient and submit UTR to record the transaction.</p>
          </div>

          {successMessage && (
            <div className="mb-6 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 px-4 py-3 rounded-xl flex items-center gap-3 animate-fade-in">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <span className="text-sm font-medium">{successMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            
            {/* Patient Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Select Patient</label>
              <select 
                required
                value={selectedPatientId}
                onChange={e => setSelectedPatientId(e.target.value)}
                className="w-full bg-black/30 border border-white/10 text-white rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">-- Search / Choose a patient --</option>
                {records.filter(r => (r.total_charge || 0) > 0).map(r => (
                  <option key={r.patient_id} value={r.patient_id}>
                    {r.patient_id} - {r.patient_name || r.his_clinical_status}
                  </option>
                ))}
              </select>
            </div>

            {/* Dynamic Due Amount */}
            {selectedPatient && (
              <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 flex justify-between items-center animate-fade-in">
                <div>
                  <div className="text-indigo-400 text-xs font-bold uppercase tracking-wider">Current Due Balance</div>
                  <div className="text-gray-400 text-xs mt-1">Total Bill: ₹{selectedPatient.total_charge?.toLocaleString() || 0}</div>
                </div>
                <div className={`text-2xl font-bold ${currentDueAmount <= 0 ? 'text-emerald-400' : 'text-white'}`}>
                  ₹{Math.max(0, currentDueAmount).toLocaleString()}
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Amount Paid (₹)</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <span className="text-gray-500">₹</span>
                </div>
                <input 
                  type="number"
                  required
                  min="1"
                  max={currentDueAmount > 0 ? currentDueAmount : undefined}
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Enter paid amount"
                  className="w-full bg-black/30 border border-white/10 text-white rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* UTR Input */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Transaction UTR Number</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <CreditCard className="w-5 h-5 text-gray-500" />
                </div>
                <input 
                  type="text"
                  required
                  minLength={12}
                  maxLength={12}
                  value={utr}
                  onChange={e => setUtr(e.target.value.replace(/\D/g, ''))} 
                  placeholder="12-digit UTR"
                  className="w-full bg-black/30 border border-white/10 text-white rounded-xl pl-12 pr-4 py-3 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 font-mono tracking-wider"
                />
              </div>
            </div>

            {/* Submit */}
            <button 
              type="submit"
              disabled={!selectedPatientId || !amount || utr.length !== 12 || currentDueAmount <= 0}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3.5 px-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(79,70,229,0.3)] hover:shadow-[0_0_25px_rgba(79,70,229,0.5)] mt-2"
            >
              <Send className="w-5 h-5" />
              Settle Due Amount
            </button>
          </form>
        </div>
      </div>

      {/* Transaction History Table */}
      {transactionHistory.length > 0 && (
        <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 mt-6 animate-fade-in">
          <h4 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-emerald-400" /> Session Transactions
          </h4>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-300">
              <thead className="bg-[#071428] text-indigo-400 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 border-b border-white/10">Time</th>
                  <th className="px-4 py-3 border-b border-white/10">Patient</th>
                  <th className="px-4 py-3 border-b border-white/10">UTR Number</th>
                  <th className="px-4 py-3 border-b border-white/10 text-right">Amount Paid</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {transactionHistory.map((txn, idx) => (
                  <tr key={idx} className="hover:bg-white/5">
                    <td className="px-4 py-3 text-gray-500">{txn.time}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{txn.name}</div>
                      <div className="text-xs text-gray-500 font-mono">{txn.id}</div>
                    </td>
                    <td className="px-4 py-3 font-mono">{txn.utr}</td>
                    <td className="px-4 py-3 text-right font-bold text-emerald-400">₹{txn.amount.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
